import { useCallback, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Table, type Column } from 'grabbitbot-legacy-ui'
import { botControlClient, statisticsClient } from '../../api'
import type { OpenPosition, PositionPage } from '../../api/statisticsTypes'
import { BotActionNotice } from '../trading/BotActionNotice'
import { useBotActions } from '../trading/botControlActions'
import { formatNumberByCoin } from '../trading/coinConfigLabels'
import { formatDateTime } from '../trading/tradingConstants'
import { coinSheetLink, pairLabel } from './positionLinks'
import { useCursorPage } from './useCursorPage'
import { failureText } from '../../api/errorMessages'

function num(value: unknown): number {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

function lossCellClass(percent: number | undefined): string | undefined {
  if (percent === undefined) return undefined
  if (percent < 0) return 'portal-pnl-negative'
  if (percent > 0) return 'portal-pnl-positive'
  return undefined
}

/**
 * Equivalent of legacy `StatisticsOpenPositionsComponent` (route
 * `.../statistics/positions/open`, contract #11 `type=open`). Per-row and
 * bulk "Close and Sell" (B4 bot control, BUSINESS-CRITICAL — force-sells a
 * real position; see `../trading/botControlActions.ts`) — no "Delete all"
 * or manual-buy here, both of those are per-coin actions that only make
 * sense on the coin sheet (`CoinSheetPage.tsx`).
 */
export function OpenPositionsPage() {
  const { exchangeId = '', tradingType = 'spot' } = useParams<{ exchangeId: string; tradingType: string }>()
  if (tradingType !== 'spot') {
    return (
      <div className="portal-card">
        <h1>Open positions</h1>
        <p className="portal-notice portal-notice--warning">Margin trading is not supported.</p>
      </div>
    )
  }
  return <SpotOpenPositions key={exchangeId} exchangeId={exchangeId} />
}

function SpotOpenPositions({ exchangeId }: { exchangeId: string }) {
  const { isBusy, feedbackFor, run } = useBotActions()
  const [selectedPositionIds, setSelectedPositionIds] = useState<string[]>([])
  const [confirmKey, setConfirmKey] = useState<string | null>(null)
  const [refreshToken, setRefreshToken] = useState(0)

  const fetcher = useCallback(
    (pageSize: number, start: string | undefined) =>
      statisticsClient.positions(exchangeId, 'open', { pageSize, start }) as Promise<PositionPage<OpenPosition>>,
    [exchangeId],
  )
  const { items, loading, error, page, hasPrev, hasNext, next, prev, pageSize } = useCursorPage<OpenPosition>(
    fetcher,
    [exchangeId, refreshToken],
  )

  function refreshPositions() {
    setRefreshToken((t) => t + 1)
  }

  function closeSelected() {
    if (selectedPositionIds.length === 0) return
    setConfirmKey(null)
    run('close:selected', () => botControlClient.closeSelectedPositions(exchangeId, selectedPositionIds), () => {
      setSelectedPositionIds([])
      refreshPositions()
    })
  }

  function closeOne(position: OpenPosition) {
    setConfirmKey(null)
    run(`close:${position.id}`, () => botControlClient.closeSelectedPositions(exchangeId, [position.id]), () => {
      refreshPositions()
    })
  }

  const columns: Column<OpenPosition>[] = [
    {
      key: 'select',
      label: '',
      sortable: false,
      format: (_v, r) => (
        <input
          type="checkbox"
          aria-label={`Select ${r.coinPair ?? pairLabel(r.coin1, r.coin2)} for closing`}
          checked={selectedPositionIds.includes(r.id)}
          onChange={(e) =>
            setSelectedPositionIds((prev) => (e.target.checked ? [...prev, r.id] : prev.filter((id) => id !== r.id)))
          }
        />
      ),
    },
    { key: 'openedAt', label: 'Open Date', align: 'left', sortValue: (r) => r.openedAt, format: (v) => formatDateTime(v as string) },
    {
      key: 'coinPair',
      label: 'Coin pair',
      align: 'left',
      sortValue: (r) => pairLabel(r.coin1, r.coin2),
      format: (_v, r) => <Link to={coinSheetLink(exchangeId, r.coin1, r.coin2)}>{r.coinPair ?? pairLabel(r.coin1, r.coin2)}</Link>,
    },
    { key: 'bid', label: 'Bid', numeric: true, sortValue: (r) => num(r.bid), format: (_v, r) => `${formatNumberByCoin(num(r.bid), r.coin2)} ${r.coin2}` },
    { key: 'price', label: 'Price', numeric: true, sortValue: (r) => num(r.price), format: (v) => num(v).toFixed(8) },
    {
      key: 'amount',
      label: 'Value (quote)',
      numeric: true,
      sortValue: (r) => num(r.amount),
      format: (_v, r) => `${formatNumberByCoin(num(r.amount), r.coin2)} ${r.coin2}`,
    },
    {
      key: 'count',
      label: 'Value (base)',
      numeric: true,
      sortValue: (r) => num(r.count),
      format: (_v, r) => `${formatNumberByCoin(num(r.count), r.coin1)} ${r.coin1}`,
    },
    {
      key: 'actualAmount',
      label: 'Actual value (quote)',
      numeric: true,
      sortValue: (r) => num(r.actualAmount),
      format: (_v, r) => (r.actualAmount === undefined ? '-' : `${formatNumberByCoin(num(r.actualAmount), r.coin2)} ${r.coin2}`),
    },
    {
      key: 'actualLossUsdt',
      label: 'Diff (buy vs actual)',
      numeric: true,
      sortValue: (r) => num(r.actualLossUsdt),
      cellClass: (_v, r) => lossCellClass(r.actualLossUsdtPercent),
      format: (_v, r) => (r.actualLossUsdt === undefined ? '-' : `${formatNumberByCoin(num(r.actualLossUsdt), 'USDT')} USDT`),
    },
    {
      key: 'actualLossUsdtPercent',
      label: 'Diff %',
      numeric: true,
      sortValue: (r) => num(r.actualLossUsdtPercent),
      cellClass: (_v, r) => lossCellClass(r.actualLossUsdtPercent),
      format: (_v, r) =>
        r.actualLossUsdtPercent === undefined ? '-' : `${r.actualLossUsdtPercent > 0 ? '+' : ''}${r.actualLossUsdtPercent}%`,
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      format: (_v, r) => {
        const key = `close:${r.id}`
        const busy = isBusy(key)
        const feedback = feedbackFor(key)
        if (confirmKey === key) {
          return (
            <span className="portal-inline-actions">
              <span>Are you sure you want to sell and close this position?</span>
              <button type="button" className="portal-button-danger" onClick={() => closeOne(r)}>
                Yes
              </button>
              <button type="button" onClick={() => setConfirmKey(null)}>
                No
              </button>
            </span>
          )
        }
        return (
          <span className="portal-inline-actions">
            <button type="button" className="portal-button-danger" disabled={busy} onClick={() => setConfirmKey(key)}>
              {busy ? 'Closing…' : 'Close and Sell'}
            </button>
            {feedback && (
              <span role={feedback.tone === 'error' ? 'alert' : 'status'} className={`portal-notice portal-notice--${feedback.tone}`}>
                {feedback.text}
              </span>
            )}
          </span>
        )
      },
    },
  ]

  return (
    <div>
      <div className="portal-page-title">
        <h1>Last Open Positions</h1>
      </div>
      <div className="portal-card">
        {loading && <p>Loading…</p>}
        {error !== null && <p role="alert">{failureText(error, 'Could not load open positions')}</p>}
        {!loading && !error && items.length === 0 && <p>No Open Positions</p>}
        {items.length > 0 && (
          <>
            <div className="portal-toolbar">
              {confirmKey === 'close:selected' ? (
                <span className="portal-inline-actions">
                  <span>Are you sure you want to sell and close these positions?</span>
                  <button type="button" className="portal-button-danger" onClick={closeSelected}>
                    Yes
                  </button>
                  <button type="button" onClick={() => setConfirmKey(null)}>
                    No
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  className="portal-button-danger"
                  disabled={selectedPositionIds.length === 0 || isBusy('close:selected')}
                  onClick={() => setConfirmKey('close:selected')}
                >
                  {isBusy('close:selected') ? 'Closing…' : 'Close and Sell selected positions'}
                </button>
              )}
            </div>
            <BotActionNotice feedback={feedbackFor('close:selected')} />
            <div className="portal-table-scroll">
              <Table columns={columns} rows={items} rowKey={(r) => r.id} />
            </div>
          </>
        )}
        <nav className="gbx-pager" aria-label="Pagination">
          <button type="button" disabled={!hasPrev} onClick={prev}>
            Previous
          </button>
          <span>
            Page {page} ({pageSize}/page)
          </span>
          <button type="button" disabled={!hasNext} onClick={next}>
            Next
          </button>
        </nav>
      </div>
    </div>
  )
}
